import { useAppDispatch } from 'app/config/storeConfig/hooks';
import { useGetUnitsQuery } from 'entities/Box';
import { useGetWarehousesForBookingQuery } from 'entities/Warehouse';
import { bookingPageActions } from '../../model/slice/bookingPageSlice';
import { useEffect, useRef } from 'react';
import { useQueryString } from 'shared/utils/helpers/convertQueryString';
import { BookingSteps, bookingHeaderActions } from 'widgets/BookingHeader';
import { warehouseMapActions } from 'widgets/SelectWarehouseMap';
import { UnitType } from 'entities/Contract';
import { useNavigate } from 'react-router-dom';
import { setBookingDataToLocalStorage } from '../helpers/setBookingDataToLocalStorage';

export const useSetBookingDataFromQuery = (): void => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    parsedQuery: { facilityId: warehouseId, boxId, sizeCodeId },
  } = useQueryString();

  const { data: warehouses } = useGetWarehousesForBookingQuery();
  const { data: boxesData } = useGetUnitsQuery({ sizeCodeId, isAvailable: true, unitType: UnitType.BOX }, { skip: !sizeCodeId });

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current || !warehouses) {
      return;
    }

    // If no warehouseId, we cannot validate or set anything
    if (!warehouseId) {
      hasInitialized.current = true;
      return;
    }

    const selectedWarehouse = warehouses.find((w) => w.warehouseId === warehouseId);

    if (selectedWarehouse) {
      dispatch(warehouseMapActions.setSelectedCard(warehouseId));
      dispatch(bookingPageActions.setWarehouseInfo(selectedWarehouse));
      dispatch(bookingHeaderActions.changeActiveStep(BookingSteps.SELECT_UNIT));
    }

    const selectedBox = boxesData?.content.find((box) => box.boxId === boxId);

    // If boxId present but box does not belong to selected warehouse or cannot be found, clear invalid params
    if (boxId && (!selectedBox || selectedBox.warehouseId !== warehouseId)) {
      setBookingDataToLocalStorage({ sizeCodeId: null, boxId: null }, navigate);
      hasInitialized.current = true;
      return;
    }

    if (selectedBox) {
      dispatch(
        bookingPageActions.setBoxInfo({
          selectedBox,
          selectedSizeCodeId: selectedBox.sizeCode.sizeCodeId,
        }),
      );
      dispatch(bookingHeaderActions.changeActiveStep(BookingSteps.CONTRACT_INFORMATION));
    }

    hasInitialized.current = true;
  }, [warehouseId, sizeCodeId, boxId, warehouses, boxesData, dispatch, navigate]);
};
